import React, { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Upload, Loader2, CheckCircle2, AlertTriangle, Download } from "lucide-react";

const STATUS_STYLES = {
  valid: "bg-emerald-100 text-emerald-800", duplicate: "bg-slate-100 text-slate-600",
  invalid: "bg-red-100 text-red-800", needs_review: "bg-amber-100 text-amber-800",
};

export function ImportDialog({ open, onOpenChange, onImported }) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [opts, setOpts] = useState({ update_matching: true, skip_duplicates: true, import_needs_review: true });
  const [result, setResult] = useState(null);

  const reset = () => { setFile(null); setPreview(null); setResult(null); };
  const onFile = async (f) => {
    if (!f) return;
    setFile(f); setPreview(null); setResult(null); setLoading(true);
    const fd = new FormData(); fd.append("file", f);
    try { const r = await api.post("/blueprints/import/preview", fd, { headers: { "Content-Type": "multipart/form-data" } }); setPreview(r.data); }
    catch (e) { toast.error(e.response?.data?.detail || "Preview failed."); }
    finally { setLoading(false); }
  };
  const commit = async () => {
    if (!file) return;
    setLoading(true);
    const fd = new FormData(); fd.append("file", file);
    fd.append("options", JSON.stringify({ ...opts, mapping: preview?.mapping || {} }));
    try { const r = await api.post("/blueprints/import/commit", fd, { headers: { "Content-Type": "multipart/form-data" } }); setResult(r.data); toast.success("Import complete."); onImported(); }
    catch (e) { toast.error(e.response?.data?.detail || "Import failed."); }
    finally { setLoading(false); }
  };
  const downloadErrors = () => {
    const errs = (preview?.errors || []);
    const csv = "row,blueprint_code,issues\n" + errs.map((e) => `${e.row},"${e.blueprint_code || ""}","${(e.issues || []).join("; ")}"`).join("\n");
    const url = window.URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a"); a.href = url; a.download = "import_errors.csv"; a.click(); window.URL.revokeObjectURL(url);
  };

  const c = preview?.counts;
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) reset(); onOpenChange(o); }}>
      <DialogContent className="max-w-3xl max-h-[88vh] overflow-y-auto" data-testid="bp-import-dialog">
        <DialogHeader><DialogTitle>Import Evidence Blueprints</DialogTitle>
          <DialogDescription>Upload an .xlsx (Evidence Blueprint sheet) or .csv. Columns are auto-mapped; review counts before importing.</DialogDescription></DialogHeader>

        {!result && (
          <div className="space-y-4">
            <label className="flex flex-col items-center justify-center gap-2 rounded-md border-2 border-dashed border-border p-6 cursor-pointer hover:bg-slate-50" data-testid="bp-import-dropzone">
              {loading && !preview ? <Loader2 className="h-6 w-6 animate-spin text-gold-dark" /> : <Upload className="h-6 w-6 text-gold-dark" />}
              <span className="text-sm text-navy font-medium">{file ? file.name : "Click to select .xlsx or .csv"}</span>
              <input type="file" accept=".xlsx,.csv" className="hidden" data-testid="bp-import-file-input" onChange={(e) => onFile(e.target.files[0])} />
            </label>

            {preview && (
              <>
                <div className="grid grid-cols-3 md:grid-cols-5 gap-2 text-center">
                  {[["Total", c.total, "bg-slate-100 text-slate-700"], ["Valid", c.valid, "bg-emerald-100 text-emerald-800"], ["Duplicates", c.duplicate, "bg-slate-100 text-slate-600"], ["Needs review", c.needs_review, "bg-amber-100 text-amber-800"], ["Invalid", c.invalid, "bg-red-100 text-red-800"]].map(([l, v, cl]) => (
                    <div key={l} className={`rounded-md p-2 ${cl}`} data-testid={`bp-import-count-${l.toLowerCase().replace(' ', '-')}`}><div className="text-xl font-bold">{v}</div><div className="text-[10px] uppercase tracking-wide">{l}</div></div>
                  ))}
                </div>

                <div className="flex flex-wrap gap-4 text-xs">
                  <label className="flex items-center gap-1.5"><Checkbox checked={opts.update_matching} onCheckedChange={(v) => setOpts({ ...opts, update_matching: !!v })} data-testid="bp-import-update" /> Update matching records</label>
                  <label className="flex items-center gap-1.5"><Checkbox checked={opts.skip_duplicates} onCheckedChange={(v) => setOpts({ ...opts, skip_duplicates: !!v })} /> Skip identical duplicates</label>
                  <label className="flex items-center gap-1.5"><Checkbox checked={opts.import_needs_review} onCheckedChange={(v) => setOpts({ ...opts, import_needs_review: !!v })} data-testid="bp-import-needsreview" /> Import rows needing review</label>
                </div>

                <div className="rounded-md border border-border overflow-hidden max-h-64 overflow-y-auto">
                  <table className="w-full text-xs"><thead className="sticky top-0"><tr className="bg-navy text-white text-left">{["Row", "Status", "Code", "Control", "Objective", "Type", "Owner", "Issues"].map((h) => <th key={h} className="px-2 py-1.5">{h}</th>)}</tr></thead>
                    <tbody>{preview.preview_rows.map((r) => (
                      <tr key={r.row} className="border-b border-border last:border-0">
                        <td className="px-2 py-1">{r.row}</td>
                        <td className="px-2 py-1"><span className={`px-1.5 py-0.5 rounded text-[10px] ${STATUS_STYLES[r.status]}`}>{r.status}</span></td>
                        <td className="px-2 py-1 font-mono">{r.blueprint_code}</td>
                        <td className="px-2 py-1 font-mono">{r.control_number}</td>
                        <td className="px-2 py-1 font-mono">{r.assessment_objective}</td>
                        <td className="px-2 py-1">{r.evidence_type}</td>
                        <td className="px-2 py-1">{r.expected_owner}</td>
                        <td className="px-2 py-1 text-amber-700">{(r.issues || []).join("; ")}</td>
                      </tr>))}</tbody>
                  </table>
                </div>
                {preview.errors.length > 0 && <Button variant="outline" size="sm" onClick={downloadErrors} data-testid="bp-import-error-report"><Download className="h-3.5 w-3.5 mr-1" /> Download error report</Button>}
              </>
            )}
          </div>
        )}

        {result && (
          <div className="space-y-3 py-4 text-center" data-testid="bp-import-result">
            <CheckCircle2 className="h-10 w-10 text-emerald-600 mx-auto" />
            <p className="text-sm text-navy font-medium">Import complete.</p>
            <div className="flex justify-center gap-3 text-sm">
              <span className="text-emerald-700">+{result.counts.created} created</span>
              <span className="text-blue-700">{result.counts.updated} updated</span>
              <span className="text-slate-600">{result.counts.skipped} skipped</span>
              <span className="text-red-700">{result.counts.invalid} invalid</span>
            </div>
          </div>
        )}

        <DialogFooter>
          {!result ? (
            <>
              <Button variant="outline" onClick={() => { reset(); onOpenChange(false); }}>Cancel</Button>
              <Button className="bg-gold text-navy hover:bg-gold-dark" disabled={!preview || loading} onClick={commit} data-testid="bp-import-commit">
                {loading ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : null} Import Valid Rows
              </Button>
            </>
          ) : <Button className="bg-navy text-white hover:bg-navy/90" onClick={() => { reset(); onOpenChange(false); }}>Done</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
